"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  EVERY_DAY,
  useLocalToday,
  WeekdayToggles,
  weekdaySet,
} from "@/components/tools/study/controls";
import { PlanSteps, PlanTimeline } from "@/components/tools/study/plan-steps";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { withLocale } from "@/lib/i18n/link-path";
import { downloadText } from "@/lib/tool-kit/download";
import {
  ASSIGNMENT_LINKS,
  ASSIGNMENT_TEMPLATES,
  ASSIGNMENT_TYPES,
  type AssignmentType,
  assignmentWorkDays,
  checkAssignmentDates,
  type PlannedStep,
  planAssignment,
} from "@/lib/tool-kit/study/assignment-plan";
import {
  dayFormatter,
  dayParts,
  isoFromDay,
  randomToken,
} from "@/lib/tool-kit/study/dates";
import {
  buildIcsCalendar,
  ICS_MIME,
  icsUidFactory,
} from "@/lib/tool-kit/study/ics";

const SITE = "https://www.citationeasy.com";
const MAX_SPAN_DAYS = 730;
const DEFAULT_SPAN_DAYS = 21;

/** English strings. A translated content file overrides these through `ui`. */
export const ASSIGNMENT_PLANNER_STRINGS = {
  typeLabel: "Assignment type",
  types: {
    "research-paper": "Research paper",
    essay: "Essay",
    "lab-report": "Lab report",
    presentation: "Presentation",
    "literature-review": "Literature review",
  },
  titleLabel: "Assignment name (optional)",
  titlePlaceholder: "e.g. HIST 210 term paper",
  startLabel: "Start date",
  dueLabel: "Due date",
  workdaysLabel: "Days you'll work on it",
  weekdaysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  workdaysHint:
    "The due date always counts, whatever the weekday — it's the day you hand in.",
  results: "Your Plan",
  printHeading: "Assignment plan: {title}",
  printDates: "{start} to {due}",
  calendarDays: "Days until due",
  workingDays: "Working days",
  steps: "Steps",
  timelineLabel: "How your working days are shared between the steps",
  stepHeading: "{number}. {name}",
  finishBy: "Finish by {date}",
  shareOfTime: "{share}% of the time",
  dayCount: "one:{count} working day|other:{count} working days",
  sharesDay: "shares its day with another step",
  dateRange: "{from} – {to}",
  useLabel: "Try:",
  downloadIcs: "Add to calendar (.ics)",
  print: "Print plan",
  copyPlan: "Copy plan",
  copied: "Copied!",
  icsFileName: "assignment-plan.ics",
  icsCalendarName: "Assignment plan",
  icsSummary: "{step} due ({number} of {total})",
  icsSummaryTitled: "{title}: {step} due ({number} of {total})",
  icsWorkOn: "Work on it: {range}",
  textHeading: "Assignment plan: {title} ({start} to {due})",
  textStep: "{number}. {name} — finish by {date} ({share}%, {days})",
  enterDates: "Choose a start date and a due date to build your plan.",
  dueBeforeStart:
    "The due date is before the start date. Pick a due date on or after the day you start.",
  tooLong:
    "That's a very long runway. Pick a due date within {max} days of the start date.",
  sameDay:
    "Start and due date are the same day, so every step lands on it. Work through them in order and keep each one short.",
  tight:
    "Fewer working days than steps, so some steps share a day. Adding working days (or starting earlier) spreads them out.",
  stepNames: {
    understand: "Understand the assignment",
    research: "Research",
    notes: "Take notes",
    outline: "Thesis and outline",
    draft: "Write the first draft",
    revise: "Revise",
    cite: "Cite and format",
    proofread: "Proofread",
    submit: "Submit",
    essayUnderstand: "Understand the prompt",
    essayResearch: "Gather evidence",
    essayOutline: "Thesis and paragraph plan",
    labUnderstand: "Read the lab brief",
    labData: "Analyse your data",
    labBackground: "Read the background",
    labOutline: "Plan the sections",
    labDraft: "Draft the report",
    presUnderstand: "Understand the brief",
    presOutline: "Storyboard the talk",
    presSlides: "Build the slides",
    presCite: "Credit your sources",
    presRehearse: "Rehearse out loud",
    presDeliver: "Present",
    litSearch: "Search the literature",
    litRead: "Read and record",
    litOutline: "Organise by theme",
    litDraft: "Write the review",
  },
  stepTips: {
    understand:
      "Reread the prompt and the rubric. Write down the length, how many and what kind of sources you need, the citation style and the exact hand-in time — and ask about anything unclear now, not the night before.",
    research:
      "Turn the topic into a question, then search your library's databases and Google Scholar. Skim abstracts before reading anything in full, and save the details of every source you might use.",
    notes:
      "Record each idea with its source and page number, and mark what is a quotation, what is a paraphrase and what is your own thought. Notes kept this way make citing quick and accidental plagiarism unlikely.",
    outline:
      "Write a working thesis — a claim you could defend in one or two sentences — and arrange your evidence under the points that support it. Ten minutes of outline saves hours of rewriting.",
    draft:
      "Write quickly and imperfectly; revision is a separate step. Start with the section you know best and leave the introduction until you know what the paper actually argues.",
    revise:
      "Fix the big things before the small ones: does every paragraph serve the thesis, is the order logical, is each claim backed by evidence? A day away from the draft first helps you read it as a stranger would.",
    cite: "Check that every in-text citation has an entry in the reference list and the other way round, then format the list, title page, headings and margins in the required style.",
    proofread:
      "Read the final version slowly — aloud, or on paper — for typos, missing words and inconsistent names or numbers. Run a spelling and grammar check, but judge each suggestion yourself.",
    submit:
      "Check the file format, the file name and the upload page, and submit a few hours early so a slow connection can't make you late. Keep a copy of exactly what you sent.",
    essayUnderstand:
      "Underline the task word — analyse, compare, evaluate, argue — and any limits on scope, then restate the question in your own words. A strong essay that answers a different question still loses marks.",
    essayResearch:
      "Collect the readings, quotations, data and examples you will use, and check that each source is credible and on point. For a short essay, a few strong sources beat a long list of weak ones.",
    essayOutline:
      "Draft a thesis that takes a clear position, then plan one paragraph per point: topic sentence, evidence, and what the evidence shows. Check the plan fits the word limit before you write.",
    labUnderstand:
      "Reread the lab manual and the marking scheme. Note the required sections, the length, the expected voice and tense, and the aim or hypothesis the experiment tested.",
    labData:
      "Process the raw data, run the calculations or statistics, and make the tables and figures first — every other section describes or explains them. Label units and axes as you go.",
    labBackground:
      "Find two or three sources that explain the theory behind the experiment and what earlier work found. You'll need them for the introduction and for comparing your results in the discussion.",
    labOutline:
      "Sketch the report section by section — introduction, methods, results, discussion — with a line on what each will say and where each table and figure goes.",
    labDraft:
      "Write the methods and results first, while the details are fresh, then the discussion, then the introduction. Write the abstract last: it summarises a report that has to exist first.",
    presUnderstand:
      "Note the time limit, the audience, the format (slides, poster, live or recorded) and how it is marked. At a typical speaking pace a ten-minute talk is about 1,300 words, which tells you how much content fits.",
    presOutline:
      "Plan the talk as a sequence of points with one main idea per slide, an opening that says why the topic matters, and a close that tells the audience what to remember.",
    presSlides:
      "Keep slide text short — the slides support what you say, they are not the script. Use one clear chart or image per point, and type large enough to read from the back of the room.",
    presCite:
      "Credit images, data and quotations on the slide where they appear, and finish with a references slide in the required style.",
    presRehearse:
      "Run the whole talk aloud several times and time each run. If you run long, cut material rather than speaking faster, and practise the transitions — that is where most talks stumble.",
    presDeliver:
      "Arrive early, test the projector and your file, and carry a backup copy on a USB stick or in your email.",
    litSearch:
      "Choose keywords and their synonyms, search two or three databases in your field, and keep a log of each search you run. Screen titles and abstracts before downloading anything.",
    litRead:
      "For each source, record the question, method, main findings and limitations in one table — a synthesis matrix — so you can see where studies agree, disagree, or leave a gap.",
    litOutline:
      "Group the sources by theme, method or debate rather than one by one, and order the groups so they lead to the gap your own work addresses.",
    litDraft:
      "Synthesise rather than summarise: each paragraph should make a point about the literature and draw on several sources to support it, not describe one study after another.",
  },
  linkLabels: {
    citationGenerator: "Citation Generator",
    citationChecker: "APA Citation Checker",
    grammarChecker: "Grammar Checker",
    essayOutline: "Essay Outline Generator",
    thesisStatement: "Thesis Statement Generator",
    researchQuestion: "Research Question Generator",
    hypothesis: "Hypothesis Generator",
    annotatedBibliography: "Annotated Bibliography Maker",
    wordCounter: "Word Counter",
    statistics: "Descriptive Statistics Calculator",
    readingTime: "Reading Time Calculator",
    teleprompter: "Online Teleprompter",
    researchPaperGuide: "How to Write a Research Paper",
    thesisGuide: "How to Write a Thesis Statement",
    findSourcesGuide: "How to Find Scholarly Sources",
    evaluateSourcesGuide: "How to Evaluate Sources",
    notesGuide: "How to Take Research Notes",
    readingPapersGuide: "How to Read Academic Papers",
    paraphrasingGuide: "Paraphrasing vs. Quoting",
    revisionGuide: "How to Revise an Academic Paper",
    methodologyGuide: "How to Write a Methodology Section",
    abstractGuide: "How to Write an Abstract",
    presentDataGuide: "How to Present Data",
    presentationCitationGuide: "How to Cite Sources in a Presentation",
    databasesGuide: "How to Use Library Databases",
    organizeGuide: "How to Organize Research",
    literatureReviewGuide: "How to Write a Literature Review",
  },
} as const;

type Strings = typeof ASSIGNMENT_PLANNER_STRINGS;
type StepKey = keyof Strings["stepNames"];

export default function AssignmentPlannerWidget() {
  const strings = useToolUi(ASSIGNMENT_PLANNER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [type, setType] = useState<AssignmentType>("research-paper");
  const [title, setTitle] = useState("");
  const [start, setStart] = useState("");
  const [due, setDue] = useState("");
  const [workdays, setWorkdays] = useState<boolean[]>([...EVERY_DAY]);

  useLocalToday((today) => {
    setStart(isoFromDay(today));
    setDue(isoFromDay(today + DEFAULT_SPAN_DAYS));
  });

  const checked = checkAssignmentDates(start, due, MAX_SPAN_DAYS);
  let dateError: string | null = null;
  if (checked.problem === "tooLong") {
    dateError = t(strings.tooLong, { max: MAX_SPAN_DAYS });
  } else if (checked.problem === "dueBeforeStart") {
    dateError = strings.dueBeforeStart;
  }
  const workDays = useMemo(
    () =>
      checked.start === null || checked.due === null
        ? []
        : assignmentWorkDays(checked.start, checked.due, weekdaySet(workdays)),
    [checked.start, checked.due, workdays]
  );
  const plan = useMemo(
    () => planAssignment(ASSIGNMENT_TEMPLATES[type], workDays),
    [type, workDays]
  );

  const spansYears =
    checked.start !== null &&
    checked.due !== null &&
    dayParts(checked.start).year !== dayParts(checked.due).year;
  const formatDay = useMemo(
    () =>
      dayFormatter(locale, {
        weekday: "short",
        month: "short",
        day: "numeric",
        ...(spansYears ? { year: "numeric" } : {}),
      }),
    [locale, spansYears]
  );

  const name = (step: PlannedStep) => strings.stepNames[step.key as StepKey];
  const tip = (step: PlannedStep) => strings.stepTips[step.key as StepKey];
  const range = (step: PlannedStep) =>
    step.first === step.due
      ? formatDay(step.due)
      : t(strings.dateRange, {
          from: formatDay(step.first),
          to: formatDay(step.due),
        });
  const days = (step: PlannedStep) =>
    tPlural(strings.dayCount, locale, step.days, { count: fmt(step.days) });
  const heading = title.trim() || strings.types[type];

  function planText(): string {
    if (checked.start === null || checked.due === null) {
      return "";
    }
    const lines = [
      t(strings.textHeading, {
        title: heading,
        start: formatDay(checked.start),
        due: formatDay(checked.due),
      }),
      "",
    ];
    for (const [index, step] of plan.entries()) {
      lines.push(
        t(strings.textStep, {
          number: index + 1,
          name: name(step),
          date: formatDay(step.due),
          share: fmt(step.share),
          days: step.days > 0 ? days(step) : strings.sharesDay,
        }),
        `   ${tip(step)}`
      );
    }
    return lines.join("\n");
  }

  function handleIcs() {
    const uid = icsUidFactory(randomToken());
    const events = plan.map((step, index) => {
      const values = {
        title: title.trim(),
        step: name(step),
        number: index + 1,
        total: plan.length,
      };
      const links = step.links.map(
        (link) =>
          `${strings.linkLabels[link]}: ${SITE}${withLocale(ASSIGNMENT_LINKS[link], locale)}`
      );
      return {
        uid: uid(index),
        start: step.due,
        summary: t(
          values.title ? strings.icsSummaryTitled : strings.icsSummary,
          values
        ),
        description: [
          tip(step),
          t(strings.icsWorkOn, { range: range(step) }),
          ...links,
        ].join("\n\n"),
      };
    });
    downloadText(
      strings.icsFileName,
      buildIcsCalendar({
        prodId: "-//CitationEasy//Assignment Planner//EN",
        name: title.trim() || strings.icsCalendarName,
        stamp: new Date(),
        events,
      }),
      ICS_MIME
    );
  }

  const ready =
    plan.length > 0 && checked.start !== null && checked.due !== null;

  return (
    <>
      <section className="grid gap-4 sm:grid-cols-2 print:hidden">
        <SelectField
          id="ap-type"
          label={strings.typeLabel}
          onChange={setType}
          options={ASSIGNMENT_TYPES.map((value) => ({
            value,
            label: strings.types[value],
          }))}
          value={type}
        />
        <TextField
          id="ap-title"
          label={strings.titleLabel}
          onChange={setTitle}
          placeholder={strings.titlePlaceholder}
          value={title}
        />
        <TextField
          id="ap-start"
          label={strings.startLabel}
          onChange={setStart}
          type="date"
          value={start}
        />
        <TextField
          id="ap-due"
          label={strings.dueLabel}
          onChange={setDue}
          type="date"
          value={due}
        />
        <div className="space-y-2 sm:col-span-2">
          <WeekdayToggles
            labels={strings.weekdaysShort}
            legend={strings.workdaysLabel}
            name="ap-day"
            onChange={setWorkdays}
            value={workdays}
          />
          <p className={HINT}>{strings.workdaysHint}</p>
        </div>
      </section>

      <section aria-live="polite" className="space-y-5">
        {dateError ? <ErrorNotice>{dateError}</ErrorNotice> : null}
        {ready || dateError ? null : <Notice>{strings.enterDates}</Notice>}
        {ready && checked.start !== null && checked.due !== null ? (
          <>
            <h2 className={`${WIDGET_H2} print:hidden`}>{strings.results}</h2>
            <div className="hidden space-y-1 print:block">
              <h2 className={WIDGET_H2}>
                {t(strings.printHeading, { title: heading })}
              </h2>
              <p className="text-sm">
                {t(strings.printDates, {
                  start: formatDay(checked.start),
                  due: formatDay(checked.due),
                })}
              </p>
            </div>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.calendarDays}
                value={fmt(checked.due - checked.start)}
              />
              <StatCard
                label={strings.workingDays}
                value={fmt(workDays.length)}
              />
              <StatCard label={strings.steps} value={fmt(plan.length)} />
            </StatGrid>
            {checked.due === checked.start ? (
              <Notice>{strings.sameDay}</Notice>
            ) : null}
            {checked.due !== checked.start && workDays.length < plan.length ? (
              <Notice>{strings.tight}</Notice>
            ) : null}
            <PlanTimeline
              label={strings.timelineLabel}
              name={name}
              plan={plan}
              totalDays={workDays.length}
            />
            <PlanSteps
              describe={{
                name,
                tip,
                range,
                days: (step) =>
                  step.days > 0 ? days(step) : strings.sharesDay,
                finishBy: (step) =>
                  t(strings.finishBy, { date: formatDay(step.due) }),
                share: (step) =>
                  t(strings.shareOfTime, { share: fmt(step.share) }),
              }}
              linkLabels={strings.linkLabels}
              plan={plan}
              stepHeading={strings.stepHeading}
              useLabel={strings.useLabel}
            />
            <div className="flex flex-wrap gap-3 print:hidden">
              <button
                className={BUTTON_PRIMARY}
                onClick={handleIcs}
                type="button"
              >
                {strings.downloadIcs}
              </button>
              <button
                className={BUTTON_SECONDARY}
                onClick={() => window.print()}
                type="button"
              >
                {strings.print}
              </button>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyPlan}
                text={planText()}
              />
            </div>
          </>
        ) : null}
      </section>
    </>
  );
}
